import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { COVER_COLORS, slugify, type CoverColor } from '@mys/contracts/constants';
import { ApiError } from '../lib/api.js';
import {
  POST_STATUS_LABELS,
  readingMinutes,
  useCreatePost,
  useDeletePost,
  usePost,
  useUpdatePost,
} from '../lib/posts.js';
import { formatDateTime } from '../lib/format.js';

/* ============================================================================
   /blog/new and /blog/:id — one editor, two modes.
   ----------------------------------------------------------------------------
   The publish controls are deliberately separate from the save button. Saving
   a draft and putting it in front of the public are different decisions, and a
   form where the second happens as a side effect of the first is a form that
   publishes something half-written.
   ========================================================================== */

/** An ISO instant as the value a `datetime-local` input wants, in LOCAL time. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

interface Fields {
  title: string;
  slug: string;
  excerpt: string;
  bodyMarkdown: string;
  coverColor: CoverColor;
}

const EMPTY: Fields = {
  title: '',
  slug: '',
  excerpt: '',
  bodyMarkdown: '',
  coverColor: 'sage',
};

export function PostEditorPage() {
  const { id } = useParams();
  const isNew = id === undefined;
  const navigate = useNavigate();

  const existing = usePost(id ?? '');
  const create = useCreatePost();
  const update = useUpdatePost(id ?? '');
  const remove = useDeletePost(id ?? '');

  const [fields, setFields] = useState<Fields>(EMPTY);
  const [loaded, setLoaded] = useState(isNew);
  /* Once someone edits the slug by hand, the title stops rewriting it. A slug
     that keeps changing under you while you fix a typo in the headline is the
     kind of thing that ends up published wrong. */
  const [slugTouched, setSlugTouched] = useState(false);
  const [schedule, setSchedule] = useState('');

  useEffect(() => {
    if (loaded || !existing.data) return;
    const post = existing.data.post;
    setFields({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      bodyMarkdown: post.bodyMarkdown,
      coverColor: post.coverColor as CoverColor,
    });
    setSlugTouched(true);
    setSchedule(post.publishedAt ? toLocalInput(post.publishedAt) : '');
    setLoaded(true);
  }, [existing.data, loaded]);

  const post = existing.data?.post;
  const mutation = isNew ? create : update;

  function set<K extends keyof Fields>(key: K, value: Fields[K]) {
    setFields((current) => ({ ...current, [key]: value }));
  }

  function setTitle(value: string) {
    setFields((current) => ({
      ...current,
      title: value,
      ...(slugTouched ? {} : { slug: slugify(value) }),
    }));
  }

  function onSave(event: FormEvent) {
    event.preventDefault();
    if (isNew) {
      create.mutate(fields, {
        onSuccess: (data) => void navigate(`/blog/${data.post.id}`, { replace: true }),
      });
      return;
    }
    /* The slug is only sent when it can change. Sending it unchanged is
       accepted by the API, but not sending it at all keeps a published post's
       save from depending on that. */
    update.mutate({
      title: fields.title,
      excerpt: fields.excerpt,
      bodyMarkdown: fields.bodyMarkdown,
      coverColor: fields.coverColor,
      ...(post?.slugEditable ? { slug: fields.slug } : {}),
    });
  }

  const fieldError = (name: string): string | undefined =>
    mutation.error instanceof ApiError ? mutation.error.fields[name] : undefined;

  const formError =
    mutation.error instanceof ApiError && Object.keys(mutation.error.fields).length === 0
      ? mutation.error.message
      : null;

  if (!isNew && existing.isPending) return <p className="muted">Loading…</p>;

  if (!isNew && existing.error) {
    return (
      <>
        <p className="alert" role="alert">
          {existing.error instanceof ApiError ? existing.error.message : 'That post was not found.'}
        </p>
        <Link to="/blog" className="btn btn--ghost">
          Back to blog
        </Link>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{isNew ? 'New post' : (post?.title ?? 'Post')}</h1>
          <p className="muted">
            {post ? POST_STATUS_LABELS[post.status] : 'Draft'}
            {post?.publishedAt && post.status === 'scheduled'
              ? ` · ${formatDateTime(post.publishedAt)} ko live hoga`
              : ''}
            {fields.bodyMarkdown ? ` · ${readingMinutes(fields.bodyMarkdown)} min read` : ''}
          </p>
        </div>
        {post?.status === 'published' ? (
          <a
            className="btn btn--ghost"
            href={`/blog/${post.slug}`}
            target="_blank"
            rel="noreferrer"
          >
            View on the site
          </a>
        ) : null}
      </div>

      <div className="stack">
        <form className="card stack" onSubmit={onSave} noValidate>
          <div className="field">
            <label htmlFor="title">Title</label>
            <input
              id="title"
              value={fields.title}
              maxLength={160}
              onChange={(event) => setTitle(event.target.value)}
            />
            {fieldError('title') ? (
              <span className="field__error" role="alert">
                {fieldError('title')}
              </span>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="slug">Slug</label>
            <input
              id="slug"
              value={fields.slug}
              maxLength={120}
              disabled={!isNew && !post?.slugEditable}
              onChange={(event) => {
                setSlugTouched(true);
                set('slug', event.target.value);
              }}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              {!isNew && !post?.slugEditable
                ? 'A live post cannot change its slug — old links would break. Unpublish it first.'
                : `The link will be /blog/${fields.slug || '…'}`}
            </span>
            {fieldError('slug') ? (
              <span className="field__error" role="alert">
                {fieldError('slug')}
              </span>
            ) : null}
          </div>

          <div className="field">
            <label htmlFor="excerpt">Excerpt</label>
            <textarea
              id="excerpt"
              value={fields.excerpt}
              maxLength={300}
              rows={2}
              onChange={(event) => set('excerpt', event.target.value)}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              This is what the blog index shows.
            </span>
          </div>

          <fieldset className="field" style={{ border: 0, margin: 0, padding: 0 }}>
            <legend style={{ padding: 0, marginBottom: 6 }}>Cover colour</legend>
            <div className="row">
              {COVER_COLORS.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={
                    fields.coverColor === value ? 'btn btn--small' : 'btn btn--ghost btn--small'
                  }
                  aria-pressed={fields.coverColor === value}
                  onClick={() => set('coverColor', value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="field">
            <label htmlFor="bodyMarkdown">Body (markdown)</label>
            <textarea
              id="bodyMarkdown"
              value={fields.bodyMarkdown}
              rows={18}
              style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 14 }}
              onChange={(event) => set('bodyMarkdown', event.target.value)}
            />
          </div>

          {formError ? (
            <p className="alert" role="alert">
              {formError}
            </p>
          ) : null}

          <div className="row">
            <button className="btn" type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </button>
            {update.isSuccess && !update.isPending ? (
              <span className="muted" role="status">
                Saved.
              </span>
            ) : null}
            <Link to="/blog" className="btn btn--ghost">
              {isNew ? 'Cancel' : 'Back to blog'}
            </Link>
          </div>
        </form>

        {/* Publishing is its own decision, not a side effect of saving. */}
        {!isNew && post ? (
          <PublishControls
            post={post}
            schedule={schedule}
            onScheduleChange={setSchedule}
            onPublish={(publishedAt) => update.mutate({ publishedAt })}
            pending={update.isPending}
          />
        ) : null}

        {!isNew && post && post.status !== 'published' ? (
          <section className="card">
            <h2 style={{ fontSize: 18, marginBottom: 8 }}>Delete</h2>
            <p className="muted" style={{ margin: '0 0 12px' }}>
              A live post cannot be deleted — unpublish it first, because a broken old link is the
              real damage.
            </p>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={remove.isPending}
              onClick={() => remove.mutate(undefined, { onSuccess: () => void navigate('/blog') })}
            >
              Delete post
            </button>
            {remove.isError ? (
              <p className="field__error" role="alert">
                {remove.error instanceof ApiError ? remove.error.message : 'Could not delete it.'}
              </p>
            ) : null}
          </section>
        ) : null}
      </div>
    </>
  );
}

function PublishControls({
  post,
  schedule,
  onScheduleChange,
  onPublish,
  pending,
}: {
  post: { status: string; publishedAt: string | null };
  schedule: string;
  onScheduleChange: (value: string) => void;
  onPublish: (publishedAt: string | null) => void;
  pending: boolean;
}) {
  return (
    <section className="card">
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>Publish</h2>

      <div className="row">
        {post.status !== 'published' ? (
          <button
            type="button"
            className="btn"
            disabled={pending}
            onClick={() => onPublish(new Date().toISOString())}
          >
            Publish now
          </button>
        ) : null}

        {post.status !== 'draft' ? (
          <button
            type="button"
            className="btn btn--ghost"
            disabled={pending}
            onClick={() => onPublish(null)}
          >
            Unpublish
          </button>
        ) : null}
      </div>

      <div className="field" style={{ marginTop: 16 }}>
        <label htmlFor="schedule">Or schedule it for later</label>
        <div className="row">
          <input
            id="schedule"
            type="datetime-local"
            value={schedule}
            onChange={(event) => onScheduleChange(event.target.value)}
          />
          <button
            type="button"
            className="btn btn--ghost btn--small"
            disabled={pending || !schedule}
            onClick={() => onPublish(new Date(schedule).toISOString())}
          >
            Schedule
          </button>
        </div>
        <span className="muted" style={{ fontSize: 13 }}>
          In your phone’s time zone. It stays private until then.
        </span>
      </div>
    </section>
  );
}
